import { useEffect, useState } from 'react';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H4 } from 'UIComponents/elements/headings';
import UIIllustration from 'UIComponents/image/UIIllustration';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import styled from 'styled-components';
import { useAppSelector } from '../../reducers';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { productVersionsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { billingNameKeySelector } from '../../selectors/ProductsSelectors';
import { eligibleAddOnSkuIdsSelector } from '../../selectors/RestrictionSelectors';
import { AddOnCard } from './AddOnCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ListContainer = styled.div.withConfig({
  displayName: "AvailableAddOnsList__ListContainer",
  componentId: "ig8mbj-0"
})(["padding:14px 0;"]);
const StyledSearchInput = styled(UISearchInput).withConfig({
  displayName: "AvailableAddOnsList__StyledSearchInput",
  componentId: "ig8mbj-1"
})(["width:100%;max-width:446px;margin-bottom:24px;"]);
const NoResults = styled.div.withConfig({
  displayName: "AvailableAddOnsList__NoResults",
  componentId: "ig8mbj-2"
})(["text-align:center;margin:14px 0;"]);
const AvailableAddOnsList = ({
  screen,
  minimumAddOnsForSearch = 5,
  highlightedProduct
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    if (highlightedProduct) {
      setSearchTerm(highlightedProduct.displayName);
    }
  }, [highlightedProduct]);
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const ownedProductVersions = useAppSelector(state => hasPricingAndPurchasingSucceeded ? productVersionsFromProductInventorySelector(state) : []);
  const ownedProductConfigurations = ownedProductVersions.map(productVersion => {
    return createProductConfig(productVersion);
  });
  const eligibleAddOnsSkuIds = useAppSelector(state => eligibleAddOnSkuIdsSelector(state, {
    selectedProductConfigurations: ownedProductConfigurations
  }));
  const filteredAddOns = useAppSelector(state => eligibleAddOnsSkuIds.map(skuId => {
    const billingNameKey = billingNameKeySelector(state, {
      skuId
    });
    return {
      skuId,
      title: I18n.text(`pricing-pages.products.${billingNameKey}.title`).toLowerCase() // use i18n text so that the search input is localized to the users language and search term
    };
  }).filter(({
    title
  }) => searchTerm ? title.includes(searchTerm.toLowerCase()) : true));
  const handleSearchInputChange = event => {
    setSearchTerm(event.target.value);
  };
  const showSearch = eligibleAddOnsSkuIds.length > minimumAddOnsForSearch || !!highlightedProduct;
  const showNoResultsMessage = hasPricingAndPurchasingSucceeded && eligibleAddOnsSkuIds.length === 0 || searchTerm && filteredAddOns.length === 0;
  const showLoadingSpinner = !hasPricingAndPurchasingSucceeded;
  return /*#__PURE__*/_jsxs(ListContainer, {
    children: [showLoadingSpinner && /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    }), showSearch && /*#__PURE__*/_jsx(UIFlex, {
      justify: "center",
      children: /*#__PURE__*/_jsx(StyledSearchInput, {
        onChange: handleSearchInputChange,
        value: searchTerm
      })
    }), showNoResultsMessage && /*#__PURE__*/_jsxs(NoResults, {
      children: [/*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.pages.addOns.search.noResults"
        })
      }), /*#__PURE__*/_jsx(UIIllustration, {
        name: "empty-state-charts",
        height: "282px",
        className: "m-all-auto"
      })]
    }), filteredAddOns.map(({
      skuId
    }) => /*#__PURE__*/_jsx(AddOnCard, {
      skuId: skuId,
      screen: screen
    }, skuId))]
  });
};
export default AvailableAddOnsList;